import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'
import { useMembersStore } from './members.js'

function flatList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.content)) return data.content
  if (Array.isArray(data?.payouts)) return data.payouts
  if (Array.isArray(data?.items)) return data.items
  return data && typeof data === 'object' ? [data] : []
}

export const usePayoutStore = defineStore('payout', () => {
  const payouts = ref([])
  const groupPayouts = ref([])
  const currentGroupId = ref(null)
  const loading = ref(false)

  function memberName(userId) {
    if (!userId) return '—'
    try {
      const m = useMembersStore().members.find((x) => String(x.userId) === String(userId))
      return m?.name || m?.displayName || m?.username || userId
    } catch (e) {
      return userId
    }
  }

  function normalizeApproval(a) {
    return {
      approverId: a?.approverId ?? a?.userId ?? null,
      approverName: a?.approverName ?? memberName(a?.approverId ?? a?.userId),
      decision: a?.decision ?? 'PENDING',
      note: a?.note ?? a?.comment ?? null,
      votedAt: a?.votedAt ?? a?.decidedAt ?? null,
      nudgeCount: a?.nudgeCount ?? 0,
      lastNudgedAt: a?.lastNudgedAt ?? null,
    }
  }

  function normalize(raw, groupId) {
    const r = raw || {}
    const approvals = Array.isArray(r.approvals)
      ? r.approvals.map(normalizeApproval)
      : []
    const currentApprovalCount =
      r.currentApprovalCount != null
        ? Number(r.currentApprovalCount)
        : approvals.filter((a) => a.decision === 'APPROVED').length
    const requiredApprovalCount =
      r.requiredApprovalCount != null
        ? Number(r.requiredApprovalCount)
        : approvals.length
    const recipientId = r.recipientId ?? r.beneficiaryId ?? null
    const initiatedBy = r.initiatedBy ?? r.creatorId ?? r.requestedBy ?? null
    const group =
      r.groupId && String(r.groupId) !== 'undefined' ? r.groupId : groupId

    return {
      id: r.id ?? r.requestId ?? r.payoutId ?? `local-${Date.now()}`,
      groupId: group,
      campaignId: r.campaignId ?? null,
      cycleId: r.cycleId ?? null,
      initiatedBy,
      initiatedByName:
        r.initiatedByName ?? r.creatorName ?? memberName(initiatedBy),
      recipientId,
      recipientName:
        r.recipientName ?? r.beneficiaryName ?? memberName(recipientId),
      amount:
        r.amount ?? r.amountCfa ?? r.value != null ? Number(r.amount ?? r.amountCfa ?? r.value) : 0,
      currency: r.currency ?? r.settlementCurrency ?? 'XOF',
      reason: r.reason ?? r.purpose ?? null,
      status: r.status ?? 'PENDING_APPROVAL',
      rejectionReason: r.rejectionReason ?? r.rejectReason ?? null,
      createdAt: r.createdAt ?? r.requestedAt ?? new Date().toISOString(),
      paidAt: r.paidAt ?? null,
      paymentReference: r.paymentReference ?? r.reference ?? null,
      currentApprovalCount,
      requiredApprovalCount,
      approvals,
    }
  }

  function upsert(fresh) {
    const i = groupPayouts.value.findIndex((p) => p.id === fresh.id)
    if (i !== -1) groupPayouts.value[i] = fresh
    else groupPayouts.value.unshift(fresh)
    return fresh
  }

  function groupIdFor(payoutId) {
    const p = groupPayouts.value.find((x) => String(x.id) === String(payoutId))
    return p?.groupId ?? currentGroupId.value
  }

  async function fetchPayouts() {
    loading.value = true
    try {
      const { data } = await client.get('/api/v1/payouts')
      payouts.value = flatList(data).map((p) => normalize(p))
      return payouts.value
    } finally {
      loading.value = false
    }
  }

  async function fetchForGroup(groupId) {
    currentGroupId.value = groupId
    loading.value = true
    try {
      const { data } = await client.get(`groups/${groupId}/payouts`)
      groupPayouts.value = flatList(data).map((p) => normalize(p, groupId))
      return groupPayouts.value
    } finally {
      loading.value = false
    }
  }

  async function fetchPayout(payoutId) {
    const known = groupPayouts.value.find((x) => String(x.id) === String(payoutId))
    if (known) return known
    const groupId = groupIdFor(payoutId)
    const { data } = await client.get(`groups/${groupId}/payouts/${payoutId}`)
    return upsert(normalize(data, groupId))
  }

  async function initiate(payload) {
    currentGroupId.value = payload.groupId
    const body = {
      campaignId: payload.campaignId ?? null,
      cycleId: payload.cycleId ?? null,
      initiatedBy: payload.initiatedBy,
      recipientId: payload.recipientId,
      amount: payload.amount,
      currency: payload.currency,
      reason: payload.reason ?? null,
    }
    const { data } = await client.post(`groups/${payload.groupId}/payouts`, body, {
      headers: { 'Idempotency-Key': crypto?.randomUUID?.() || String(Date.now()) },
    })
    const fresh = normalize(data, payload.groupId)
    upsert(fresh)
    return fresh
  }

  async function decide(payoutId, decision, viewerId, note) {
    const groupId = groupIdFor(payoutId)
    const { data } = await client.post(
      `groups/${groupId}/payouts/${payoutId}/decision`,
      {
        decision,
        comment: note || null,
        actorId: viewerId,
      },
    )
    return upsert(normalize(data, groupId))
  }

  async function approve(payoutId, viewerId, note) {
    return decide(payoutId, 'APPROVED', viewerId, note)
  }

  async function reject(payoutId, viewerId, note) {
    return decide(payoutId, 'REJECTED', viewerId, note)
  }

  async function nudge(payoutId, approverId, viewerId) {
    const groupId = groupIdFor(payoutId)
    const { data } = await client.post(
      `groups/${groupId}/payouts/${payoutId}/nudge`,
      { approverId, actorId: viewerId },
    )
    return upsert(normalize(data, groupId))
  }

  async function cancel(payoutId, viewerId) {
    const groupId = groupIdFor(payoutId)
    const { data } = await client.post(
      `groups/${groupId}/payouts/${payoutId}/cancel`,
      { actorId: viewerId },
    )
    return upsert(normalize(data, groupId))
  }

  return {
    payouts,
    groupPayouts,
    loading,
    fetchPayouts,
    fetchForGroup,
    fetchPayout,
    initiate,
    approve,
    reject,
    nudge,
    cancel,
  }
})